import { useEffect, useRef, useState, useCallback } from 'react';
import { createElement } from 'react';
import './style.less';
function ImageView(props) {
    const [isLongImg, setIsLongImg] = useState(false);
    const [transform, setTransform] = useState({
        scale: 1,
        translateX: 0,
        translateY: 0,
    });
    const [lastTap, setLastTap] = useState(0);
    const maskRef = useRef(null);
    const imgRef = useRef(null);
    const initialDistance = useRef(0);
    const initialTransform = useRef(transform);
    const transformRef = useRef(transform);
    const touchStartPos = useRef({ x: 0, y: 0 });
    const animationFrame = useRef();
    useEffect(() => {
        transformRef.current = transform;
    }, [transform]);
    const onImageLoad = useCallback(() => {
        const img = imgRef.current;
        const container = maskRef.current;
        if (!img || !container)
            return;
        const { naturalHeight: imgHeight, naturalWidth: imgWidth } = img;
        const containerWidth = container.clientWidth;
        const renderedHeight = (containerWidth / imgWidth) * imgHeight;
        setIsLongImg(renderedHeight > window.innerHeight * 2.5 || imgHeight / imgWidth >= 3);
    }, []);
    const checkBoundaries = useCallback((newX, newY) => {
        const img = imgRef.current;
        const container = maskRef.current;
        if (!img || !container)
            return { x: newX, y: newY };
        const scale = transformRef.current.scale;
        const imgRect = img.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const maxX = Math.max((imgRect.width * scale - containerRect.width) / 2, 0);
        const maxY = Math.max((imgRect.height * scale - containerRect.height) / 2, 0);
        return {
            x: Math.min(Math.max(newX, -maxX), maxX),
            y: Math.min(Math.max(newY, -maxY), maxY),
        };
    }, []);
    const handleClick = (e) => {
        const now = Date.now();
        if (now - lastTap < 300) {
            e.stopPropagation();
            e.preventDefault();
        }
        setLastTap(now);
    };
    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            touchStartPos.current = { x: touch.clientX, y: touch.clientY };
        }
        else if (e.touches.length === 2) {
            const t1 = e.touches[0];
            const t2 = e.touches[1];
            initialDistance.current = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
            initialTransform.current = transformRef.current;
        }
    }, []);
    const handleTouchMove = useCallback((e) => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
        }
        animationFrame.current = requestAnimationFrame(() => {
            if (e.touches.length === 1 && transformRef.current.scale > 1) {
                const touch = e.touches[0];
                const deltaX = touch.clientX - touchStartPos.current.x;
                const deltaY = touch.clientY - touchStartPos.current.y;
                setTransform(prev => {
                    const newX = prev.translateX + deltaX;
                    const newY = prev.translateY + deltaY;
                    const bounded = checkBoundaries(newX, newY);
                    return {
                        ...prev,
                        translateX: bounded.x,
                        translateY: bounded.y,
                    };
                });
                touchStartPos.current = { x: touch.clientX, y: touch.clientY };
            }
            else if (e.touches.length === 2) {
                const t1 = e.touches[0];
                const t2 = e.touches[1];
                const currentDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                if (initialDistance.current > 0) {
                    const newScale = Math.max(0.5, Math.min((currentDistance / initialDistance.current) *
                        initialTransform.current.scale, 3));
                    setTransform(prev => ({
                        ...prev,
                        scale: newScale,
                    }));
                }
            }
        });
    }, [checkBoundaries]);
    const handleTouchEnd = useCallback(() => {
        initialDistance.current = 0;
        setTransform(prev => {
            if (prev.scale === 1)
                return { ...prev, translateX: 0, translateY: 0 };
            const bounded = checkBoundaries(prev.translateX, prev.translateY);
            return { ...prev, ...bounded };
        });
    }, [checkBoundaries]);
    useEffect(() => {
        const handleResize = () => {
            setTransform({ scale: 1, translateX: 0, translateY: 0 });
            onImageLoad();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [onImageLoad]);
    return (createElement("div", { className: 'image-view-wrap', ref: maskRef, onClick: props.onClick, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd },
        createElement("div", { className: 'image-view', style: isLongImg
                ? { alignItems: 'flex-start', height: 'auto' }
                : { height: '100vh', alignItems: 'center' } },
            createElement("img", { src: props.src, alt: "", onLoad: onImageLoad, onClick: handleClick, ref: imgRef, style: {
                    transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
                    transformOrigin: 'center',
                    maxWidth: '100%',
                    transition: transform.scale === 1
                        ? 'transform 0.3s cubic-bezier(0.2, 0, 0.2, 1)'
                        : 'none',
                    touchAction: 'none',
                } }))));
}
export default ImageView;
